/**
 * Modal 對話框元件
 *
 * 提供統一的模態對話框功能：
 * - 背景遮罩
 * - 標題和內容區域
 * - 關閉按鈕
 * - 動畫效果
 * - 鍵盤支援（ESC 關閉）
 */

import { escapeHtml } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalOptions {
  /** 對話框大小 */
  size?: ModalSize;
  /** 是否可透過點擊背景關閉 */
  closeOnOverlay?: boolean;
  /** 是否可透過 ESC 關閉 */
  closeOnEscape?: boolean;
  /** 是否顯示關閉按鈕 */
  showCloseButton?: boolean;
  /** 關閉時的回呼 */
  onClose?: () => void;
  /** 開啟時的回呼 */
  onOpen?: () => void;
}

// ========================================================================
// 樣式常數
// ========================================================================

const SIZE_STYLES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  full: 'max-w-full mx-4',
};

// ========================================================================
// Modal 類別
// ========================================================================

export class Modal {
  private overlay: HTMLDivElement;
  private container: HTMLDivElement;
  private header: HTMLDivElement;
  private body: HTMLDivElement;
  private footer: HTMLDivElement | null = null;
  private isOpen: boolean = false;
  private options: Required<Omit<ModalOptions, 'onClose' | 'onOpen'>> & Pick<ModalOptions, 'onClose' | 'onOpen'>;
  private boundEscHandler: (e: KeyboardEvent) => void;

  constructor(title: string, options: ModalOptions = {}) {
    this.options = {
      size: options.size ?? 'md',
      closeOnOverlay: options.closeOnOverlay ?? true,
      closeOnEscape: options.closeOnEscape ?? true,
      showCloseButton: options.showCloseButton ?? true,
      onClose: options.onClose,
      onOpen: options.onOpen,
    };

    this.boundEscHandler = this.handleEscape.bind(this);
    this.overlay = this.createOverlay();
    this.container = this.createContainer();
    this.header = this.createHeader(title);
    this.body = this.createBody();

    this.container.appendChild(this.header);
    this.container.appendChild(this.body);
    // 注意：container 已在 createContainer() 中掛載到 flex 容器，不需要再次掛載
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  /**
   * 取得 Modal 內容區域（用於掛載內容）
   */
  getBody(): HTMLDivElement {
    return this.body;
  }

  /**
   * 取得或建立 Footer 區域
   */
  getFooter(): HTMLDivElement {
    if (!this.footer) {
      this.footer = document.createElement('div');
      this.footer.className = 'px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3';
      this.container.appendChild(this.footer);
    }
    return this.footer;
  }

  /**
   * 設定 Modal 內容
   * 🔒 安全性：使用 DOMParser 驗證 HTML 字串,防止 XSS 攻擊
   */
  setContent(content: string | HTMLElement): void {
    this.body.innerHTML = '';

    if (typeof content === 'string') {
      // 使用 DOMParser 安全地解析 HTML
      const parser = new DOMParser();
      try {
        const doc = parser.parseFromString(content, 'text/html');

        // 檢查是否有解析錯誤
        const parserError = doc.querySelector('parsererror');
        if (parserError) {
          console.error('[Modal] Invalid HTML content:', parserError);
          // Fallback: 安全地顯示為純文字
          this.body.textContent = content;
          return;
        }

        // 安全地添加解析後的節點
        Array.from(doc.body.childNodes).forEach(node => {
          this.body.appendChild(node.cloneNode(true));
        });
      } catch (error) {
        console.error('[Modal] Failed to parse HTML content:', error);
        // Fallback: 安全地顯示為純文字
        this.body.textContent = content;
      }
    } else {
      // 直接添加 HTMLElement
      this.body.appendChild(content);
    }
  }

  /**
   * 設定標題
   */
  setTitle(title: string): void {
    const titleElement = this.header.querySelector('h2');
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  /**
   * 開啟對話框
   */
  open(): void {
    if (this.isOpen) return;

    document.body.appendChild(this.overlay);
    document.body.style.overflow = 'hidden';

    // 動畫進入
    requestAnimationFrame(() => {
      this.overlay.classList.remove('opacity-0');
      this.container.classList.remove('opacity-0', 'scale-95');
      this.container.classList.add('opacity-100', 'scale-100');
    });

    if (this.options.closeOnEscape) {
      document.addEventListener('keydown', this.boundEscHandler);
    }

    this.isOpen = true;
    this.options.onOpen?.();
  }

  /**
   * 關閉對話框
   */
  close(): void {
    if (!this.isOpen) return;

    // 動畫退出
    this.overlay.classList.add('opacity-0');
    this.container.classList.remove('opacity-100', 'scale-100');
    this.container.classList.add('opacity-0', 'scale-95');

    setTimeout(() => {
      this.overlay.remove();
      document.body.style.overflow = '';
    }, 200);

    if (this.options.closeOnEscape) {
      document.removeEventListener('keydown', this.boundEscHandler);
    }

    this.isOpen = false;
    this.options.onClose?.();
  }

  /**
   * 切換開啟/關閉狀態
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * 銷毀對話框
   */
  destroy(): void {
    this.close();
    document.removeEventListener('keydown', this.boundEscHandler);
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private createOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className = [
      'fixed',
      'inset-0',
      'z-50',
      'overflow-y-auto',
      'bg-black',
      'bg-opacity-50',
      'transition-opacity',
      'duration-200',
      'opacity-0',
    ].join(' ');

    overlay.innerHTML = '<div class="flex min-h-full items-center justify-center p-4"></div>';

    if (this.options.closeOnOverlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay || e.target === overlay.firstElementChild) {
          this.close();
        }
      });
    }

    return overlay;
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = [
      'relative',
      'w-full',
      SIZE_STYLES[this.options.size],
      'bg-white',
      'rounded-xl',
      'shadow-xl',
      'transform',
      'transition-all',
      'duration-200',
      'opacity-0',
      'scale-95',
    ].join(' ');

    // 阻止點擊事件冒泡到 overlay
    container.addEventListener('click', (e) => e.stopPropagation());

    // 掛載到 overlay 的 flex 容器中
    const flexContainer = this.overlay.querySelector('div');
    flexContainer?.appendChild(container);

    return container;
  }

  private createHeader(title: string): HTMLDivElement {
    const header = document.createElement('div');
    header.className = 'flex items-center justify-between px-6 py-4 border-b border-gray-200';

    header.innerHTML = `
      <h2 class="text-lg font-semibold text-gray-900">${title}</h2>
      ${this.options.showCloseButton ? `
        <button type="button" class="text-gray-400 hover:text-gray-600 transition-colors">
          <span class="sr-only">關閉</span>
          <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      ` : ''}
    `;

    const closeBtn = header.querySelector('button');
    closeBtn?.addEventListener('click', () => this.close());

    return header;
  }

  private createBody(): HTMLDivElement {
    const body = document.createElement('div');
    body.className = 'px-6 py-4 max-h-[70vh] overflow-y-auto';
    return body;
  }

  private handleEscape(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.close();
    }
  }
}

// ========================================================================
// 確認對話框
// ========================================================================

export interface ConfirmOptions {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger';
}

/**
 * 顯示確認對話框
 */
export function confirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;

    const resolveOnce = (value: boolean) => {
      if (resolved) return;
      resolved = true;
      resolve(value);
    };

    const modal = new Modal(options.title ?? '確認', {
      size: 'sm',
      closeOnOverlay: false,
      onClose: () => resolveOnce(false),
    });

    // 🔒 安全性：轉義 message 防止 XSS
    const safeMessage = escapeHtml(message);
    modal.setContent(`<p class="text-gray-600">${safeMessage}</p>`);

    // 🔒 安全性：轉義按鈕文字
    const safeCancelText = escapeHtml(options.cancelText ?? '取消');
    const safeConfirmText = escapeHtml(options.confirmText ?? '確認');

    const footer = modal.getFooter();
    footer.innerHTML = `
      <button type="button" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
        ${safeCancelText}
      </button>
      <button type="button" class="px-4 py-2 text-sm font-medium text-white ${options.confirmVariant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'} rounded-lg transition-colors">
        ${safeConfirmText}
      </button>
    `;

    const [cancelBtn, confirmBtn] = footer.querySelectorAll('button');

    cancelBtn?.addEventListener('click', () => {
      resolveOnce(false);
      modal.close();
    });

    confirmBtn?.addEventListener('click', () => {
      resolveOnce(true);
      modal.close();
    });

    modal.open();
  });
}

// ========================================================================
// 提示對話框
// ========================================================================

export interface PromptOptions {
  title?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
}

/**
 * 顯示輸入提示對話框
 */
export function prompt(message: string, options: PromptOptions = {}): Promise<string | null> {
  return new Promise((resolve) => {
    let resolved = false;

    const resolveOnce = (value: string | null) => {
      if (resolved) return;
      resolved = true;
      resolve(value);
    };

    const modal = new Modal(options.title ?? '輸入', {
      size: 'sm',
      closeOnOverlay: false,
      onClose: () => resolveOnce(null),
    });

    const body = modal.getBody();
    body.innerHTML = `
      <div class="space-y-4">
        <p class="text-gray-600">${message}</p>
        <input
          type="text"
          class="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
          placeholder="${options.placeholder ?? ''}"
          value="${options.defaultValue ?? ''}"
        >
      </div>
    `;

    const input = body.querySelector('input') as HTMLInputElement;

    const footer = modal.getFooter();
    footer.innerHTML = `
      <button type="button" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
        ${options.cancelText ?? '取消'}
      </button>
      <button type="button" class="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
        ${options.confirmText ?? '確認'}
      </button>
    `;

    const [cancelBtn, confirmBtn] = footer.querySelectorAll('button');

    cancelBtn?.addEventListener('click', () => {
      resolveOnce(null);
      modal.close();
    });

    confirmBtn?.addEventListener('click', () => {
      resolveOnce(input.value);
      modal.close();
    });

    // Enter 鍵確認
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        resolveOnce(input.value);
        modal.close();
      }
    });

    modal.open();

    // 自動聚焦輸入框
    setTimeout(() => input.focus(), 100);
  });
}
